/**
 * Self-Learning Module Configuration
 *
 * ML model monitoring, feedback management, and continuous improvement.
 * Provides visibility into model accuracy, drift, and retraining needs.
 */

import { ModuleConfig } from './moduleTypes'

export const selfLearningConfig: ModuleConfig = {
  id: 'self-learning',
  name: 'Self-Learning Engine',
  description: 'ML model monitoring and continuous improvement',
  icon: 'GraduationCap',
  basePath: '/learning',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'overview',
      path: '',
      name: 'Model Overview',
      icon: 'LayoutDashboard',
      description: 'Summary of model performance and health',
      component: 'ModelOverview',
      requiredPermissions: ['models:read'],
      position: 1,
    },
    {
      id: 'accuracy',
      path: 'accuracy',
      name: 'Model Accuracy',
      icon: 'Target',
      description: 'Accuracy metrics and confusion matrix analysis',
      component: 'ModelAccuracyDashboard',
      requiredPermissions: ['models:read'],
      position: 2,
    },
    {
      id: 'feedback',
      path: 'feedback',
      name: 'Feedback Queue',
      icon: 'MessageSquare',
      description: 'Pending predictions awaiting validation',
      component: 'FeedbackQueue',
      requiredPermissions: ['models:write'],
      position: 3,
      badge: {
        type: 'count',
        dataKey: 'pendingFeedbackCount',
      },
    },
    {
      id: 'retraining',
      path: 'retraining',
      name: 'Retraining Status',
      icon: 'RefreshCw',
      description: 'Model retraining pipeline and history',
      component: 'RetrainingStatus',
      requiredPermissions: ['models:read'],
      position: 4,
    },
    {
      id: 'drift',
      path: 'drift',
      name: 'Drift Detection',
      icon: 'TrendingDown',
      description: 'Data and concept drift monitoring',
      component: 'DriftMonitor',
      requiredPermissions: ['models:read'],
      position: 5,
    },
    {
      id: 'knowledge',
      path: 'knowledge',
      name: 'Knowledge Capture',
      icon: 'BookOpen',
      description: 'Document domain knowledge for model improvement',
      component: 'KnowledgeCapture',
      requiredPermissions: ['knowledge:write'],
      position: 6,
    },
    {
      id: 'performance-history',
      path: 'history',
      name: 'Performance History',
      icon: 'History',
      description: 'Historical model performance trends',
      component: 'PerformanceHistory',
      requiredPermissions: ['models:read'],
      position: 7,
    },
  ],

  workflows: [
    {
      id: 'model-health-check',
      name: 'Model Health Check',
      description: 'Systematic review of model performance',
      icon: 'HeartPulse',
      steps: [
        {
          id: 'review-overview',
          name: 'Review Overview',
          description: 'Check overall model health metrics',
          navigateTo: 'overview',
          icon: 'LayoutDashboard',
          estimatedDuration: 3,
        },
        {
          id: 'check-accuracy',
          name: 'Check Accuracy',
          description: 'Review accuracy metrics and confusion matrix',
          navigateTo: 'accuracy',
          icon: 'Target',
          estimatedDuration: 5,
        },
        {
          id: 'monitor-drift',
          name: 'Monitor Drift',
          description: 'Check for data or concept drift',
          navigateTo: 'drift',
          icon: 'TrendingDown',
          estimatedDuration: 5,
        },
        {
          id: 'process-feedback',
          name: 'Process Feedback',
          description: 'Validate pending predictions',
          navigateTo: 'feedback',
          icon: 'MessageSquare',
          estimatedDuration: 15,
        },
        {
          id: 'assess-retraining',
          name: 'Assess Retraining Need',
          description: 'Determine if retraining is needed',
          navigateTo: 'retraining',
          icon: 'RefreshCw',
          estimatedDuration: 5,
        },
      ],
      triggerConditions: ['weeklyReview', 'accuracyDropped', 'driftDetected'],
    },
    {
      id: 'feedback-processing',
      name: 'Feedback Processing',
      description: 'Validate predictions and improve models',
      icon: 'MessageSquare',
      steps: [
        {
          id: 'review-queue',
          name: 'Review Feedback Queue',
          description: 'Sort and prioritize pending feedback',
          navigateTo: 'feedback',
          icon: 'List',
          estimatedDuration: 5,
        },
        {
          id: 'validate-predictions',
          name: 'Validate Predictions',
          description: 'Mark predictions as correct/incorrect',
          icon: 'CheckCircle',
          estimatedDuration: 20,
        },
        {
          id: 'capture-knowledge',
          name: 'Capture Domain Knowledge',
          description: 'Document learnings from false positives/negatives',
          navigateTo: 'knowledge',
          icon: 'BookOpen',
          estimatedDuration: 10,
        },
        {
          id: 'review-impact',
          name: 'Review Impact',
          description: 'Check how feedback improved accuracy',
          navigateTo: 'accuracy',
          icon: 'Target',
          estimatedDuration: 5,
        },
      ],
      triggerConditions: ['feedbackQueueThreshold', 'dailyReview'],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'performance-summary',
        name: 'Current Performance',
        template: '{modelTerminology} accuracy: {accuracy}% (target: {targetAccuracy}%). {modelsLoaded}/{totalModels} {assetTerm} models active. Trend: {trendDirection} ({changePercent}% vs previous period). Primary metric: {targetMetric}.',
        order: 1,
        required: true,
      },
      {
        id: 'feedback-analysis',
        name: 'Feedback Analysis',
        template: 'Feedback processed: {processedCount}. True Positives: {tpPercent}%, True Negatives: {tnPercent}%, False Positives: {fpPercent}%, False Negatives: {fnPercent}%.',
        order: 2,
      },
      {
        id: 'drift-status',
        name: 'Drift Status',
        template: '{driftContext}: {dataDriftStatus}. Concept drift: {conceptDriftStatus}. {driftExplanation}',
        order: 3,
        condition: 'hasDriftData',
      },
      {
        id: 'optimization-actions',
        name: 'Optimization Actions',
        template: '**{modelTerminology} Optimization Actions:**\n• {action1}\n• {action2}\n• {action3}',
        order: 4,
        condition: 'hasOptimizationNeeds',
      },
      {
        id: 'quality-assurance',
        name: 'Quality Assurance',
        template: 'Gap to {targetMetric} target: {gapToTarget}%. {qualityRecommendation}',
        order: 5,
        required: true,
      },
    ],
    availableVariables: [
      'accuracy', 'targetAccuracy', 'modelsLoaded', 'totalModels', 'trendDirection', 'changePercent',
      'processedCount', 'tpPercent', 'tnPercent', 'fpPercent', 'fnPercent',
      'dataDriftStatus', 'conceptDriftStatus', 'driftExplanation',
      'action1', 'action2', 'action3',
      'gapToTarget', 'qualityRecommendation',
      'modelTerminology', 'targetMetric', 'assetTerm', 'driftContext',
    ],
  },

  industryOverrides: {
    manufacturing: {
      narrativeOverrides: {
        modelTerminology: 'Equipment failure predictor',
        targetMetric: 'OEE',
        assetTerm: 'production equipment',
        driftContext: 'Production parameter drift',
      },
    },
    oil_gas: {
      narrativeOverrides: {
        modelTerminology: 'Process upset predictor',
        targetMetric: 'Equipment Availability',
        assetTerm: 'field equipment',
        driftContext: 'Process condition drift',
      },
    },
    healthcare: {
      additionalSubPages: [
        {
          id: 'clinical-validation',
          path: 'clinical',
          name: 'Clinical Validation',
          icon: 'Stethoscope',
          description: 'Clinical validation of device predictions',
          component: 'ClinicalValidation',
          position: 8,
        },
      ],
      narrativeOverrides: {
        modelTerminology: 'Device failure predictor',
        targetMetric: 'Device Uptime',
        assetTerm: 'medical device',
        driftContext: 'Clinical parameter drift',
      },
    },
    financial: {
      additionalSubPages: [
        {
          id: 'backtesting',
          path: 'backtest',
          name: 'Backtesting',
          icon: 'Rewind',
          description: 'Historical backtesting of risk models',
          component: 'ModelBacktesting',
          position: 8,
        },
      ],
      narrativeOverrides: {
        modelTerminology: 'Risk scoring model',
        targetMetric: 'Portfolio Default Rate',
        assetTerm: 'trading system',
        driftContext: 'Market regime drift',
      },
    },
    energy: {
      narrativeOverrides: {
        modelTerminology: 'Grid stability predictor',
        targetMetric: 'Grid Reliability Index',
        assetTerm: 'generation asset',
        driftContext: 'Load pattern drift',
      },
    },
    transportation: {
      narrativeOverrides: {
        modelTerminology: 'Fleet breakdown predictor',
        targetMetric: 'Fleet Availability',
        assetTerm: 'vehicle',
        driftContext: 'Route condition drift',
      },
    },
    mining: {
      narrativeOverrides: {
        modelTerminology: 'Equipment breakdown predictor',
        targetMetric: 'Equipment Utilization',
        assetTerm: 'heavy equipment',
        driftContext: 'Operating condition drift',
      },
    },
    chemicals: {
      narrativeOverrides: {
        modelTerminology: 'Process upset predictor',
        targetMetric: 'Reactor Yield',
        assetTerm: 'reactor/vessel',
        driftContext: 'Reaction parameter drift',
      },
    },
    food_beverage: {
      narrativeOverrides: {
        modelTerminology: 'Line stoppage predictor',
        targetMetric: 'Line Efficiency',
        assetTerm: 'production line',
        driftContext: 'Product quality drift',
      },
    },
    pharmaceuticals: {
      narrativeOverrides: {
        modelTerminology: 'Batch quality predictor',
        targetMetric: 'Batch Yield',
        assetTerm: 'GMP equipment',
        driftContext: 'Process qualification drift',
      },
    },
    aerospace: {
      narrativeOverrides: {
        modelTerminology: 'Component reliability predictor',
        targetMetric: 'Aircraft Dispatch Reliability',
        assetTerm: 'aircraft system',
        driftContext: 'Operational envelope drift',
      },
    },
    metals: {
      narrativeOverrides: {
        modelTerminology: 'Mill equipment predictor',
        targetMetric: 'Production Yield',
        assetTerm: 'mill equipment',
        driftContext: 'Process temperature drift',
      },
    },
    iot_telecom: {
      name: 'Connectivity Intelligence',
      description: 'ML models for connectivity prediction and network optimization',
      icon: 'Brain',
      additionalWorkflows: [
        {
          id: 'connectivity-model-validation',
          name: 'Connectivity Model Validation',
          description: 'Validate and improve connectivity prediction models',
          icon: 'CheckCircle',
          steps: [
            {
              id: 'review-accuracy',
              name: 'Review Model Accuracy',
              description: 'Check overall model performance metrics',
              navigateTo: 'accuracy',
              icon: 'Target',
              estimatedDuration: 5,
            },
            {
              id: 'process-feedback',
              name: 'Process Feedback',
              description: 'Review feedback from operators and field teams',
              navigateTo: 'feedback',
              icon: 'MessageSquare',
              estimatedDuration: 10,
            },
            {
              id: 'check-drift',
              name: 'Check for Drift',
              description: 'Monitor for model drift',
              navigateTo: 'drift',
              icon: 'TrendingDown',
              estimatedDuration: 5,
            },
            {
              id: 'review-knowledge',
              name: 'Review Knowledge Base',
              description: 'Check knowledge base for network-specific insights',
              navigateTo: 'knowledge',
              icon: 'BookOpen',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['weeklyReview', 'accuracyDropped', 'newCarrierAdded'],
        },
      ],
      narrativeOverrides: {
        modelTerminology: 'Connectivity failure predictor',
        targetMetric: 'Fleet Connectivity Rate',
        assetTerm: 'SIM/device',
        driftContext: 'Network condition drift',
      },
    },
    utilities: {
      narrativeOverrides: {
        modelTerminology: 'Infrastructure failure predictor',
        targetMetric: 'Service Reliability',
        assetTerm: 'distribution asset',
        driftContext: 'Load pattern drift',
      },
    },
    pulp_paper: {
      narrativeOverrides: {
        modelTerminology: 'Equipment degradation predictor',
        targetMetric: 'Machine Efficiency',
        assetTerm: 'paper machine',
        driftContext: 'Pulp quality drift',
      },
    },
    automotive: {
      narrativeOverrides: {
        modelTerminology: 'Assembly defect predictor',
        targetMetric: 'First Pass Yield',
        assetTerm: 'assembly station',
        driftContext: 'Process parameter drift',
      },
    },
    datacenter: {
      narrativeOverrides: {
        modelTerminology: 'Infrastructure failure predictor',
        targetMetric: 'Server Uptime',
        assetTerm: 'server rack',
        driftContext: 'Thermal and power load drift',
      },
    },
  },

  requiredPermissions: ['models:read'],
}
